package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import dev.lingvo.site.ds.Motion
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener

/** One live instruction panel: updates never wait for an exit or leave duplicate controls behind. */
internal class SetupContentResponse {
    var element: HTMLElement? = null
    private var previousKey: String? = null
    private var animation: dynamic = null

    fun cancel() {
        animation?.cancel()
        animation = null
    }

    fun update(key: String, pointer: Boolean) {
        val changed = previousKey != null && previousKey != key
        previousKey = key
        if (!changed) return
        val panel = element ?: return
        val running = animation?.playState == "running"
        val current = window.getComputedStyle(panel)
        val fromTransform = if (running) current.transform else "translateY(4px)"
        val fromOpacity = if (running) current.opacity else "0.9"
        cancel()
        if (!pointer || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
        if (jsTypeOf(panel.asDynamic().animate) != "function") return
        val start = js("({})")
        start.transform = fromTransform
        start.opacity = fromOpacity
        val end = js("({transform:'translateY(0)',opacity:1})")
        val options = js("({})")
        options.duration = Motion.StandardMs
        options.easing = Motion.EaseOut
        // No fill: the underlying style is always fully visible, even if a tab stops painting.
        animation = panel.asDynamic().animate(arrayOf(start, end), options)
    }
}

@Composable
internal fun rememberSetupContentResponse(key: String, pointer: Boolean): SetupContentResponse {
    val response = remember { SetupContentResponse() }
    DisposableEffect(response) {
        val media = window.matchMedia("(prefers-reduced-motion: reduce)")
        val listener = EventListener { if (media.matches) response.cancel() }
        media.addEventListener("change", listener)
        onDispose {
            media.removeEventListener("change", listener)
            response.cancel()
            response.element = null
        }
    }
    SideEffect { response.update(key, pointer) }
    return response
}
